import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Grid, Card, CardContent,
  Tabs, Tab, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Chip, CircularProgress, Alert, Avatar,
  IconButton, Tooltip, Divider, TextField,
} from '@mui/material';
import ArrowBackIcon     from '@mui/icons-material/ArrowBack';
import AccessTimeIcon    from '@mui/icons-material/AccessTime';
import EventNoteIcon     from '@mui/icons-material/EventNote';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NoteIcon          from '@mui/icons-material/Note';
import { alpha } from '@mui/material/styles';
import { principalAPI }  from '../../services/api';

const fmt     = (n) => `₹${(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—';

const SESSION_STATUS_COLOR  = { open: 'warning', completed: 'success', void: 'default' };
const CARE_STATUS_COLOR     = { pending: 'warning', acknowledged: 'info', completed: 'success' };
const CARE_PRIORITY_COLOR   = { normal: 'default', high: 'warning', urgent: 'error' };
const MOOD_COLOR            = { Happy: 'success', Calm: 'info', Energetic: 'primary', Tired: 'warning', Upset: 'error', Sick: 'error' };

function StatCard({ label, value, sub, color = 'primary.main', live }) {
  return (
    <Card elevation={1} sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center', py: 2 }}>
        {live && (
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} mb={0.5}>
            <FiberManualRecordIcon sx={{ fontSize: 10, color: 'success.main' }} />
            <Typography variant="caption" color="success.main" fontWeight={600}>LIVE</Typography>
          </Box>
        )}
        <Typography variant="h5" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {sub && <Typography variant="caption" color="text.disabled" display="block">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

function SessionsTab({ sessions }) {
  if (!sessions.length) {
    return (
      <Box textAlign="center" py={6}>
        <AccessTimeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No sessions recorded this month</Typography>
      </Box>
    );
  }
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Date</strong></TableCell>
            <TableCell><strong>Check-in</strong></TableCell>
            <TableCell><strong>Check-out</strong></TableCell>
            <TableCell align="center"><strong>Hours</strong></TableCell>
            <TableCell align="right"><strong>Amount</strong></TableCell>
            <TableCell align="center"><strong>Status</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sessions.map(s => (
            <TableRow key={s.id} hover>
              <TableCell>{fmtDate(s.check_in)}</TableCell>
              <TableCell>{fmtTime(s.check_in)}</TableCell>
              <TableCell>{s.check_out ? fmtTime(s.check_out) : <Chip label="In Daycare" color="success" size="small" />}</TableCell>
              <TableCell align="center">{s.billed_hours ?? '—'}</TableCell>
              <TableCell align="right">{s.amount ? fmt(s.amount) : '—'}</TableCell>
              <TableCell align="center">
                <Chip label={s.status} color={SESSION_STATUS_COLOR[s.status] || 'default'} size="small" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DailyUpdatesTab({ updates }) {
  if (!updates.length) {
    return (
      <Box textAlign="center" py={6}>
        <EventNoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No daily updates posted this month</Typography>
      </Box>
    );
  }
  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {updates.map(u => (
        <Paper key={u.id} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
            <Typography variant="subtitle2" fontWeight={700}>
              {fmtDate(u.update_date)}
            </Typography>
            <Box display="flex" gap={0.5} flexWrap="wrap">
              {u.mood && <Chip label={u.mood} color={MOOD_COLOR[u.mood] || 'default'} size="small" />}
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mb: 1 }}>{u.content}</Typography>
          {(u.meals || u.nap || u.activities) && (
            <Box display="flex" gap={2} flexWrap="wrap">
              {u.meals      && <Typography variant="caption" color="text.secondary">🍱 <strong>Meals:</strong> {u.meals}</Typography>}
              {u.nap        && <Typography variant="caption" color="text.secondary">😴 <strong>Nap:</strong> {u.nap}</Typography>}
              {u.activities && <Typography variant="caption" color="text.secondary">🎨 <strong>Activities:</strong> {u.activities}</Typography>}
            </Box>
          )}
          <Divider sx={{ mt: 1.5, mb: 0.5 }} />
          <Typography variant="caption" color="text.disabled">
            Posted by {u.posted_by_name || 'Teacher'}
          </Typography>
        </Paper>
      ))}
    </Box>
  );
}

function CareNotesTab({ careNotes }) {
  if (!careNotes.length) {
    return (
      <Box textAlign="center" py={6}>
        <NoteIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
        <Typography color="text.secondary">No care notes for this month</Typography>
      </Box>
    );
  }
  return (
    <TableContainer sx={{ overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell><strong>Date</strong></TableCell>
            <TableCell><strong>Title</strong></TableCell>
            <TableCell><strong>Note</strong></TableCell>
            <TableCell align="center"><strong>Priority</strong></TableCell>
            <TableCell align="center"><strong>Status</strong></TableCell>
            <TableCell><strong>Completion Remark</strong></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {careNotes.map(n => (
            <TableRow key={n.id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(n.note_date)}</TableCell>
              <TableCell>{n.title || '—'}</TableCell>
              <TableCell sx={{ maxWidth: 240 }}>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {n.note_text}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Chip label={n.priority} color={CARE_PRIORITY_COLOR[n.priority] || 'default'} size="small" />
              </TableCell>
              <TableCell align="center">
                <Chip label={n.status} color={CARE_STATUS_COLOR[n.status] || 'default'} size="small" />
              </TableCell>
              <TableCell sx={{ maxWidth: 180 }}>
                <Typography variant="caption" color="text.secondary">
                  {n.completion_remark || '—'}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default function DaycareStudentProfile() {
  const { studentId } = useParams();
  const navigate      = useNavigate();

  const now = new Date();
  const [monthValue, setMonthValue] = useState(
    `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  );
  const [tab,     setTab]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [data,    setData]    = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [year, month] = monthValue.split('-').map(Number);
      const res = await principalAPI.daycare.getStudentProfile(studentId, { year, month });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load student profile');
    } finally {
      setLoading(false);
    }
  }, [studentId, monthValue]);

  useEffect(() => { load(); }, [load]);

  const student = data?.student;
  const stats   = data?.stats;
  const todayMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box component="main" sx={{ flexGrow: 1, p: 3, maxWidth: 1100, mx: 'auto' }}>

        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Tooltip title="Back to Daycare Management">
            <IconButton onClick={() => navigate('/principal/daycare')} size="small">
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <Typography variant="body2" color="text.secondary">Daycare Management</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading && !data ? (
          <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>
        ) : student && (
          <>
            <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 22, fontWeight: 700 }}>
                  {student.name?.[0]?.toUpperCase()}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={700}>{student.name}</Typography>
                  <Box display="flex" gap={1} flexWrap="wrap" mt={0.5}>
                    <Chip label={student.admission_number} size="small" variant="outlined" />
                    {student.class_name && (
                      <Chip label={`${student.class_name}${student.class_section ? ' – ' + student.class_section : ''}`} size="small" color="primary" variant="outlined" />
                    )}
                    <Chip
                      label={student.daycare_active ? 'Active' : (student.daycare_active === false ? 'Inactive' : 'Not enrolled')}
                      color={student.daycare_active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                <Box textAlign="right">
                  {student.parent_name && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Parent: {student.parent_name}
                    </Typography>
                  )}
                  {student.enrolled_at && (
                    <Typography variant="caption" color="text.disabled" display="block">
                      Enrolled: {fmtDate(student.enrolled_at)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>

            <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
              <Typography variant="subtitle1" fontWeight={600}>Viewing:</Typography>
              <TextField
                type="month"
                size="small"
                value={monthValue}
                onChange={e => e.target.value && setMonthValue(e.target.value)}
                inputProps={{ max: todayMonth }}
                sx={{ width: 180 }}
              />
              {loading && <CircularProgress size={20} />}
            </Box>

            {stats && (
              <Grid container spacing={2} mb={3}>
                <Grid item xs={6} sm={3}>
                  <StatCard label="Sessions This Month" value={stats.session_count} color="primary.main" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard label="Total Hours" value={`${stats.total_hours}h`} color="info.main" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard label="Amount Billed" value={fmt(stats.total_billed)} color="success.main" />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <StatCard
                    label="Currently In Daycare"
                    value={stats.open_session ? 'Yes' : 'No'}
                    color={stats.open_session ? 'success.main' : 'text.secondary'}
                    live={!!stats.open_session}
                    sub={stats.open_session ? `Since ${fmtTime(stats.open_session.check_in)}` : undefined}
                  />
                </Grid>
              </Grid>
            )}

            <Paper sx={{ mb: 3, p: 0.75 }}>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
                TabIndicatorProps={{ sx: { display: 'none' } }}
                sx={{
                  minHeight: 0,
                  '& .MuiTabs-flexContainer': { gap: 1 },
                  '& .MuiTab-root': {
                    minHeight: 0,
                    borderRadius: 999,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2.5,
                    py: 1,
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
                    '&:hover': {
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.2)
                    },
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        bgcolor: 'primary.dark'
                      }
                    }
                  }
                }}
              >
                <Tab icon={<AccessTimeIcon />} iconPosition="start"
                  label={`Sessions (${data?.sessions?.length ?? 0})`} />
                <Tab icon={<EventNoteIcon />} iconPosition="start"
                  label={`Daily Updates (${data?.daily_updates?.length ?? 0})`} />
                <Tab icon={<NoteIcon />} iconPosition="start"
                  label={`Care Notes (${data?.care_notes?.length ?? 0})`} />
              </Tabs>
            </Paper>

            <Paper elevation={1} sx={{ borderRadius: 2 }}>
              <Box p={3}>
                {tab === 0 && <SessionsTab sessions={data?.sessions || []} />}
                {tab === 1 && <DailyUpdatesTab updates={data?.daily_updates || []} />}
                {tab === 2 && <CareNotesTab careNotes={data?.care_notes || []} />}
              </Box>
            </Paper>
          </>
        )}
      </Box>
    </Box>
  );
}
